import React, { useEffect, useState } from "react";
import api from "../api/api";

const BlockchainLogPage = () => {
    const [message, setMessage] = useState("");
    const [shipmentId, setShipmentId] = useState(1);
    const [logs, setLogs] = useState([]);
    const [validation, setValidation] = useState(null);
    const [response, setResponse] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadLogs();
    }, []);

    const loadLogs = async () => {
        try {
            const res = await api.get("/blockchainlog");
            setLogs(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error(e);
        }
    };

    const validateChain = async () => {
        try {
            setError("");
            const res = await api.get("/blockchainlog/validate");
            setValidation(res.data);
        } catch (e) {
            setError(e?.response?.data?.message || "Failed to validate blockchain.");
        }
    };

    const saveLog = async () => {
        setError("");
        setResponse("");

        if (!shipmentId || Number(shipmentId) <= 0) {
            setError("Shipment ID must be greater than 0.");
            return;
        }

        if (!message.trim()) {
            setError("Message cannot be empty.");
            return;
        }

        try {
            setLoading(true);

            const payloadToSend = {
                shipmentId: Number(shipmentId),
                data: message.trim(),
                description: "User blockchain log",
            };

            const res = await api.post("/blockchainlog", payloadToSend);

            setResponse(
                res.data?.transactionHash
                    ? `✔ Saved to blockchain. Hash: ${res.data.transactionHash}`
                    : "✔ Saved to blockchain!"
            );

            setMessage("");
            await loadLogs();
        } catch (e) {
            console.error(e);
            setError(
                e?.response?.data?.message ||
                e?.message ||
                "Failed to save blockchain log"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto mt-8 p-6 bg-white rounded shadow">
            <h2 className="text-2xl font-bold mb-4">Blockchain Logging</h2>

            <div className="border rounded p-4 mb-6">
                <label className="block mb-2">Shipment ID</label>
                <input
                    type="number"
                    value={shipmentId}
                    onChange={(e) => setShipmentId(e.target.value)}
                    className="border p-2 w-full mb-3"
                />

                <textarea
                    className="border p-2 w-full h-24"
                    placeholder="Enter log message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                />

                <button
                    onClick={saveLog}
                    disabled={loading}
                    className="w-full mt-4 bg-purple-600 text-white p-2 rounded disabled:opacity-50"
                >
                    {loading ? "Saving..." : "Save to Blockchain"}
                </button>
            </div>

            <button
                onClick={validateChain}
                className="mb-4 bg-green-700 text-white px-4 py-2 rounded"
            >
                Validate Blockchain
            </button>

            {validation && (
                <div className="mb-4 p-3 border rounded bg-gray-50">
                    <b>Validation:</b>{" "}
                    {validation.valid ? "Valid" : "Invalid"} — {validation.message}
                </div>
            )}

            {error && <p className="text-red-500 mt-3">{error}</p>}
            {response && <p className="text-green-600 mt-3">{response}</p>}

            <h3 className="text-xl font-bold mt-6 mb-3">Blockchain Log History</h3>

            <table className="w-full border text-sm">
                <thead>
                    <tr>
                        <th className="border p-2">ID</th>
                        <th className="border p-2">Shipment</th>
                        <th className="border p-2">Action</th>
                        <th className="border p-2">Description</th>
                        <th className="border p-2">Hash</th>
                        <th className="border p-2">Previous Hash</th>
                        <th className="border p-2">Created</th>
                    </tr>
                </thead>
                <tbody>
                    {logs.map((log) => (
                        <tr key={log.id}>
                            <td className="border p-2">{log.id}</td>
                            <td className="border p-2">{log.shipmentId ?? "—"}</td>
                            <td className="border p-2">{log.action || log.eventType || "—"}</td>
                            <td className="border p-2">{log.description || log.data || "—"}</td>
                            <td className="border p-2 break-all">{log.hash || log.transactionHash || "—"}</td>
                            <td className="border p-2 break-all">{log.previousHash || "—"}</td>
                            <td className="border p-2">
                                {log.createdAt || log.timestamp
                                    ? new Date(log.createdAt || log.timestamp).toLocaleString()
                                    : "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default BlockchainLogPage;